import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from './Button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './Dialog';
import { IconButton } from './IconButton';
import { cn } from './utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './DropdownMenu';
import { useLayoutPreferences, type TableActionsMode } from '@/contexts/LayoutPreferencesContext';

export type TableActionsProps = {
    onEdit?: () => void;
    onDelete?: () => void | Promise<void>;
    deleteTitle?: string;
    deleteDescription?: string;
    disabled?: boolean;
    className?: string;
    mode?: 'auto' | TableActionsMode;
};

export function TableActions({
    onEdit,
    onDelete,
    deleteTitle = 'Excluir registro',
    deleteDescription = 'Tem certeza de que deseja excluir este registro? Esta ação não poderá ser desfeita.',
    disabled = false,
    className,
    mode = 'auto',
}: TableActionsProps) {
    const { preferences } = useLayoutPreferences();
    const displayMode = mode === 'auto' ? preferences.tableActionsMode : mode;
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    async function handleConfirmDelete() {
        if (!onDelete || deleting) return;
        setDeleting(true);
        try {
            await onDelete();
            setConfirmOpen(false);
        } catch {
            // A tela de origem apresenta a mensagem retornada pela API.
        } finally {
            setDeleting(false);
        }
    }

    if (!onEdit && !onDelete) {
        return null;
    }

    return (
        <div className={cn('nx-table-actions-group', displayMode === 'buttons' && 'nx-table-actions-group--buttons', className)}>
            {displayMode === 'dropdown' && <DropdownMenu>
                <DropdownMenuTrigger asChild><IconButton size="sm" variant="ghost" label="Ações" disabled={disabled || deleting}><MoreHorizontal size={16} aria-hidden /></IconButton></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Ações</DropdownMenuLabel><DropdownMenuSeparator />
                    {onEdit && <DropdownMenuItem onSelect={onEdit} disabled={disabled || deleting}><Pencil size={15} aria-hidden />Editar</DropdownMenuItem>}
                    {onDelete && <DropdownMenuItem className="nx-dropdown-item--danger" onSelect={() => setConfirmOpen(true)} disabled={disabled || deleting}><Trash2 size={15} aria-hidden />Excluir</DropdownMenuItem>}
                </DropdownMenuContent>
            </DropdownMenu>}
            {displayMode === 'buttons' && onEdit ? (
                <IconButton label="Editar" onClick={onEdit} disabled={disabled}>
                    <Pencil size={15} strokeWidth={1.9} aria-hidden />
                </IconButton>
            ) : null}

            {onDelete ? (
                <Dialog open={confirmOpen} onOpenChange={(next) => !deleting && setConfirmOpen(next)}>
                    {displayMode === 'buttons' && <IconButton
                        label="Excluir"
                        variant="danger"
                        onClick={() => setConfirmOpen(true)}
                        disabled={disabled}
                    >
                        <Trash2 size={15} strokeWidth={1.9} aria-hidden />
                    </IconButton>}
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>{deleteTitle}</DialogTitle>
                            <DialogDescription>{deleteDescription}</DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <DialogClose asChild>
                                <Button type="button" variant="ghost" disabled={deleting}>
                                    Cancelar
                                </Button>
                            </DialogClose>
                            <Button type="button" variant="danger" loading={deleting} loadingText="Excluindo..." onClick={handleConfirmDelete}>
                                Excluir
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            ) : null}
        </div>
    );
}
